import React, {useState, useEffect, useRef} from 'react';
import {
  ScrollView,
  View,
  Image,
  ImageBackground,
  Text,
  Pressable,
} from 'react-native';
import {useIsFocused} from '@react-navigation/native';

import RecordList from '../Shared/RecordList';
import QuickAddList from '../Shared/QuickAddList';
import Icon from '../Shared/Icon';
import {ReportsStyleSheet} from '../../Styles/ReportsStyles';
import {CommonStyleSheet} from '../../Styles/Shared/CommonStyles';
import {Images} from '../../Constants/Images';
import Colors from '../../Styles/Shared/Colors';
import {usePowerSyncData} from '../../Providers/PowerSyncDataProvider';
import {TemplateCategoryTypes} from '../../Constants/TemplateCategoryTypes';
import {Submission} from '../../Models/PowerSyncModels/models';
import FormList from '../Shared/FormList';
import {
  CATEGORY,
  METADATA_KEYS,
  PERMISSIONS,
} from '../../Constants/AppConstants';
import {usePermission} from '../../Providers/PermissionProvider';
import {useRelation} from '../../Providers/SubmissionRelationProvider';
import {FilterTypes} from '../../Constants/General';
import {usePowerSyncUser} from '../../Providers/PowerSyncUserProvider';
import {FilterValue} from '../Shared/SearchBox';

export type SortKey = 'date' | 'incidentId' | 'az';

export interface SortConfig {
  key: SortKey;
  direction: 'ascending' | 'descending';
}

export declare type ReportsProps = {
  navigation: any;
};

const Reports = (props: ReportsProps): React.ReactElement => {
  const [reports, setReports] = useState<Submission[]>();
  const {templateCategories, people, submissions, templates, powersyncReady} = usePowerSyncData();
  const [filterValue, setFilterValue] = useState<FilterValue>({
    startDate: '',
    endDate: '',
    statuses: [],
    types: [],
    person: '',
    firstAidAttendant: '',
    project: '',
    equipmentType: '',
    equipment: '',
    pendingInvestigation: false,
    investigationAttached: false,
  });
  const [sortConfig, setSortConfig] = React.useState<SortConfig>({
    key: 'date',
    direction: 'descending',
  });

  const user = usePowerSyncUser();
  const isFocused = useIsFocused();
  const supportEmail = process.env.REACT_APP_SUPPORT_EMAIL;
  const [noReportsFound, setNoReportsFound] = useState(false);
  const {hasPermission} = usePermission();
  const {getRelatedSubmissions} = useRelation();

  const fetchSubmissions = async () => {
    if (!submissions.length) return;
    let viewReport = hasPermission(PERMISSIONS.REPORT_VIEW);

    let templateCategory = templateCategories
      .find(t => t.name === TemplateCategoryTypes.REPORTS);
      
    let listTemplateIds = templateCategory?.listTemplateIds ?? [];

    let filteredTemplates = templates
      .filter(
        t =>
          listTemplateIds.includes(t._id),
      )
      .map(t => t.name);
      
    setNoReportsFound(filteredTemplates.length === 0);
    let filteredSubmissions = submissions.filter(s =>
      filteredTemplates.includes(s.templateVersion.name!),
    );
    //If no view permissions can only see forms that user created
    if (!viewReport) {
      let userEmail = user.getUserInfo().email;
      let personLoggedIn = people.find(x => x.email == userEmail);
      if (personLoggedIn) {
        let submissionIds = await getRelatedSubmissions(personLoggedIn._id);
        filteredSubmissions = filteredSubmissions.filter(
          x => submissionIds.includes(x._id) || x.createdBy == userEmail,
        );
      }
    }
    setReports(filteredSubmissions);
  };

  useEffect(() => {
    const fetchAsync = async () => {
      await fetchSubmissions();
    };
    fetchAsync();
  }, [powersyncReady]);

  useEffect(() => {
    const fecthAsync = async () => {
      if (isFocused) {
        await fetchSubmissions();
      }
    };
    fecthAsync();
  }, [isFocused]);

  const unselectCommand = useRef({
    unselect: () => {},
    setUnselectCommand(func: () => void) {
      this.unselect = func;
    },
  });

  if (!isFocused) return <></>;

  return (
    <React.Fragment key={'report'}>
      <ScrollView style={{backgroundColor: Colors.white}}>
        <Pressable
          style={{
            //@ts-ignore
            cursor: 'auto',
          }}
          onPress={() => {
            unselectCommand.current.unselect();
          }}>
          <>
            <View>
              <ImageBackground
                source={Images.REPORTS}
                resizeMode='cover'
                style={{height: 260}}>
                <Text style={ReportsStyleSheet.headerTitle}>Reports</Text>
              </ImageBackground>
            </View>
            <View style={{flexDirection: 'row'}}>
              <View style={{flex: 2, padding: 28}}>
                {noReportsFound && supportEmail && (
                  <Text
                    style={[
                      ReportsStyleSheet.listSubtitle,
                      {textTransform: 'none', letterSpacing: 0},
                    ]}>
                    {'No reports found. Please contact Support at ' +
                      supportEmail +
                      ' to get reports added to your account.'}
                  </Text>
                )}
                <RecordList
                  records={reports}
                  showDrafts={true}
                  hasMetaData={true}
                  showGroup={true}
                  category={CATEGORY.REPORTS}
                  metaDataKey_title={METADATA_KEYS.TITLE}
                  metaDataKey_project={METADATA_KEYS.PROJECT_NAME}
                  showFilters={true}
                  showSorters={true}
                  showInvestigation={true}
                  filters={[
                    {label: 'Date Range', type: FilterTypes.DATE_RANGE},
                    {label: 'Person Involved', type: FilterTypes.PERSON},
                    {
                      label: 'First Aid Attendant',
                      type: FilterTypes.FIRST_AID_ATTENDANT,
                    },
                    {label: 'Project/Site', type: FilterTypes.PROJECT},
                    {label: 'Type', type: FilterTypes.REPORT_TYPE},
                    {label: '', type: FilterTypes.INVESTIGATION},
                  ]}
                  navigation={props.navigation}
                  setUnselectCommand={(func: () => void) => {
                    unselectCommand.current.setUnselectCommand(func);
                  }}
                  filterValue={filterValue}
                  onChangeFilter={filterValue => setFilterValue(filterValue)}
                  sortConfig={sortConfig}
                  setSortConfig={setSortConfig}
                />
              </View>
              <View style={{flex: 1}}>
                <View style={ReportsStyleSheet.fabContainer}>
                  <FormList
                    templateCategoryName={TemplateCategoryTypes.REPORTS}
                    navigation={props.navigation}
                    category={CATEGORY.REPORTS}
                  />
                </View>
                <View style={CommonStyleSheet.bcsfFooter}>
                  <Image
                    source={Images.BCSF}
                    style={CommonStyleSheet.bcsfFooterImage}
                  />
                  <Text style={CommonStyleSheet.bcsfFooterText}>
                    BC <Text style={{fontWeight: '700'}}>Forest Safety</Text>
                  </Text>
                </View>
              </View>
            </View>
          </>
        </Pressable>
      </ScrollView>
      <QuickAddList navigation={props.navigation} />
    </React.Fragment>
  );
};

export default Reports;
